import type { AccessReviewStatus, Actor, ApprovalRequest, AuditEntry, Entitlements, Group, Role, TenantUser } from '@titan/shared';
import { useSearchParams } from 'react-router';
import { Segmented } from '../../ui/index.js';
import { AuditList } from '../org/AuditList.js';
import { GroupsTab } from './GroupsTab.js';
import { PeopleTab } from './PeopleTab.js';
import { RolesTab } from './RolesTab.js';
import styles from './Access.module.css';

export type AccessTab = 'people' | 'groups' | 'roles' | 'activity';

export interface AccessTenant {
  id: string;
  name: string;
  seats: number;
  allowedDomains: string[];
  modules: Entitlements;
  ssoLabel: string | null;
}

export interface AccessCenterProps {
  tenant: AccessTenant;
  users: TenantUser[];
  groups: Group[];
  roles: Role[];
  audit: AuditEntry[];
  approvals: ApprovalRequest[];
  accessReview: AccessReviewStatus | null;
  actor: Actor;
  /** Staff previewing a tenant pass its id to the org endpoints. */
  staffTenantId?: string;
  canWrite: boolean;
  onChanged: () => Promise<void>;
}

const TABS: AccessTab[] = ['people', 'groups', 'roles', 'activity'];

/**
 * One place for identity on both surfaces: who is here, how they are grouped, what each
 * role grants, and what changed. People belong to groups, groups hold roles, roles set a
 * level per module. Sensitive changes wait for a second person.
 */
export function AccessCenter(props: AccessCenterProps) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: AccessTab = TABS.includes(raw as AccessTab) ? (raw as AccessTab) : 'people';
  const setTab = (t: AccessTab) => {
    const next = new URLSearchParams(params);
    if (t === 'people') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
  };
  const pending = props.approvals.length;

  return (
    <div className={styles.stack}>
      <Segmented<AccessTab>
        aria-label="Access"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'people', label: `People · ${props.users.length}${pending ? ` · ${pending} pending` : ''}` },
          { value: 'groups', label: `Groups · ${props.groups.length}` },
          { value: 'roles', label: `Roles · ${props.roles.length}` },
          { value: 'activity', label: 'Activity' },
        ]}
      />
      {tab === 'people' && <PeopleTab {...props} />}
      {tab === 'groups' && <GroupsTab {...props} />}
      {tab === 'roles' && <RolesTab {...props} />}
      {tab === 'activity' && <AuditList audit={props.audit} />}
    </div>
  );
}
